/**
 * Cloudflare Turnstile: a token proving a browser asks, checked by `lead-diagnostic`.
 * Site key in `VITE_TURNSTILE_SITE_KEY` (widget 0x4AAAAAAFpmhUBonw5ci8XQ; Cloudflare's test key
 * 1x00000000000000000000BB with the test secret 1x0000000000000000000000000000000AA also works).
 */

interface TurnstileApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not load')));
    script.onerror = () => { loading = null; reject(new Error('Turnstile did not load')); };
    document.head.appendChild(script);
  });
  return loading;
};

export const turnstileSiteKey = (): string | null =>
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim() || null;

/** Checked by the server: a token only counts for the surface it was asked for. */
export type TurnstileAction = 'diagnostic_code' | 'diagnostic_signin' | 'diagnostic_start';

/** One single-use token per call: renders the widget in `container`, runs it, removes it. */
export const getTurnstileToken = async (container: HTMLElement, action: TurnstileAction): Promise<string> => {
  const siteKey = turnstileSiteKey();
  if (!siteKey) throw new Error('Missing VITE_TURNSTILE_SITE_KEY');
  const api = await loadTurnstile();
  return new Promise<string>((resolve, reject) => {
    const widgetId = api.render(container, {
      sitekey: siteKey,
      action,
      // Invisible or managed widget (set on the key at Cloudflare): shown only when Cloudflare
      // asks the person to click.
      appearance: 'interaction-only',
      execution: 'execute',
      // Removed after Turnstile is done with its own callback (removing inside it makes
      // Turnstile reset a widget that no longer exists).
      callback: (token: string) => { window.setTimeout(() => api.remove(widgetId), 0); resolve(token); },
      'error-callback': (code: string) => {
        window.setTimeout(() => api.remove(widgetId), 0);
        reject(new Error(`Turnstile failed (${code})`));
        return true; // Handled: no automatic retry behind the person's back.
      },
    });
    api.execute(widgetId);
  });
};
